import { ReactNode, useId } from "react";
import {
  ControllerRenderProps,
  FieldPath,
  FieldValues,
  useController,
  useFormContext,
} from "react-hook-form";
import { InputField } from "../forms/input";
import { FormErrorMessage } from "./form-error-message";

export type { FieldPath, FieldValues } from "react-hook-form";

export type FormFieldRenderer<
  TFieldValues extends FieldValues = FieldValues,
  TFieldPath extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = (
  props: Omit<ControllerRenderProps<TFieldValues, TFieldPath>, "ref"> & {
    id: string;
    required?: boolean;
    className?: string;
  },
) => ReactNode;

export interface FormFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TFieldPath extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  id?: string;
  name: TFieldPath;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  children: ReactNode | FormFieldRenderer<TFieldValues, TFieldPath>;
  className?: string;
  inputClassName?: string;
}

export type TFormField<
  TFieldValues extends FieldValues = FieldValues,
  TFieldPath extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<FormFieldProps<TFieldValues, TFieldPath>, "children">;

export const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TFieldPath extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  id: idProp,
  name,
  label,
  required,
  disabled,
  children,
  className,
  inputClassName,
}: FormFieldProps<TFieldValues, TFieldPath>) => {
  const { control } = useFormContext();
  const defaultId = useId();
  const id = idProp ?? defaultId;

  const rules = required
    ? { required: `${label ?? name} is required` }
    : undefined;

  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    disabled,
    rules,
  });

  return (
    <InputField id={id} label={label} required={required} className={className}>
      {typeof children === "function"
        ? children({ ...field, id, required, className: inputClassName })
        : children}
      {error && <FormErrorMessage error={error} />}
    </InputField>
  );
};
